/**
 * GA4 event payload builders for Native Interactive Site Map (ISM) tracking.
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `googleAnalytics4Event(...)`. Most events share
 * GA4's generic `native_map_events` name, differentiated by `click_target`/`click_target1`/
 * `click_target2`/`click_target3` — the same convention
 * `pages/searchResults/components/eventTracking/googleAnalytics4EventsHandler.js` uses
 * elsewhere in this app. `buildLotSelectedEvent` and `buildCardsScrolledEvent` are the exceptions
 * in the source table — they get their own dedicated event names, not the generic one.
 *
 * @module nativeInteractiveSiteMap/services/googleAnalytics4EventBuilder
 */
import getTabTrackingValue from './tabTrackingValue';
import getFloorPlanDetailTabTrackingValue from './floorPlanDetailTabTrackingValue';

/**
 * @param {object} params
 * @param {string} params.interactionType - 'zoom_in' | 'zoom_out' | 'pan' | 'map_type_changed'
 * @param {string} params.inputMethod - 'mouse' | 'touch' | 'button'
 * @param {string} [params.mapType] - 'road' | 'satellite' — only set for map_type_changed
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildMapInteractionEvent({ interactionType, inputMethod, mapType }) {
  const customProperties = {
    click_target: interactionType,
    click_target1: inputMethod,
  };

  if (mapType) {
    customProperties.click_target2 = `map_type_${mapType}`;
  }

  return { eventName: 'native_map_events', customProperties };
}

/**
 * @param {object} params
 * @param {string|null} params.selectedChip - newly selected chip key (pill path) or the selected
 *   lot's status (lot-selection path) — becomes `search_value` on both paths
 * @param {string|null} params.previousChip - the chip key that was active before (pill path only)
 * @param {number} params.resultsCount - lot count for the current view
 * @param {number} params.cardCount - number of cards rendered in the active tab
 * @param {'lot_status_type'|'lot_selection'} params.source - what updated the panel
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelUpdatedEvent({ selectedChip, previousChip, resultsCount, cardCount, source, isMobile }) {
  const customProperties = {
    click_target: 'lot_status_type_selected',
    search_value: selectedChip,
    click_target2: resultsCount,
    click_target3: cardCount,
    position: source === 'lot_selection' ? 'map_lot' : 'map_pills',
    filter_name: `panel_type_${isMobile ? 'sheet' : 'panel'}`,
  };

  if (previousChip != null) {
    customProperties.click_target1 = `previouslot_${previousChip}`;
  }

  return { eventName: 'native_map_events', customProperties };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelScrollEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'lot_status_type_scrolled' },
  };
}

/**
 * Card grid horizontal scroll — gets its own dedicated event name, unlike the other non-lot
 * events above which share the generic native_map_events name.
 * @param {object} params
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildCardsScrolledEvent({ isMobile }) {
  return {
    eventName: 'native_map_card_scrolled',
    customProperties: {
      filter_name: `panel_type_${isMobile ? 'sheet' : 'panel'}`,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.selectedTab - internal tab constant (constants.js)
 * @param {number} params.resultsCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildTabSelectedEvent({ selectedTab, resultsCount }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'available_tab_selected',
      click_target1: getTabTrackingValue(selectedTab),
      click_target2: resultsCount,
    },
  };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyWhenAvailableClickedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'notify_when_available_clicked' },
  };
}

/**
 * Mobile drawer interaction — a vertical drag or the drawer being opened. Reuses the generic
 * `native_map_events` name with its own `click_target`, like the other non-lot events above.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildDrawerDraggedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'map_sheet_dragged' },
  };
}

/**
 * Encodes the lot attributes into `click_target1`'s `key:value;key:value` format, skipping any
 * attribute that's missing — the lot map inventory API can return `null` or `''` for lotType/
 * lotSize/lotFacing (see services/processLotMapInventory.js), and a trailing `f:` with nothing
 * after it is worse than just leaving that attribute out.
 * @param {{ plansCount: number, lotType: string|null, lotSize: number|null, lotFacing: string|null }} params
 * @returns {string}
 */
function encodeLotAttributes({ plansCount, lotType, lotSize, lotFacing }) {
  return [
    ['c', plansCount],
    ['t', lotType],
    ['s', lotSize],
    ['f', lotFacing],
  ]
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${key}:${value}`)
    .join(';');
}

/**
 * @param {object} params
 * @param {object} params.lot - raw store lot object (see services/processLotMapInventory.js)
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.plansCount
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLotSelectedEvent({ lot, planId, specId, plansCount, isMobile }) {
  return {
    eventName: isMobile ? 'native_map_lot_selected_m' : 'native_map_lot_selected_d',
    customProperties: {
      plan_id: planId,
      spec_id: specId,
      lot_id: lot.lotId,
      lot_number: lot.displayLabel,
      lot_group: lot.lotGroupName,
      lot_status: lot.status,
      click_target1: encodeLotAttributes({
        plansCount,
        lotType: lot.lotType,
        lotSize: lot.lotSize,
        lotFacing: lot.lotFacing,
      }),
    },
  };
}

/**
 * Fires when the user lands on the Floor Plan Detail view (initial entry, or switching to a
 * different plan while already there). Gets its own dedicated event name, unlike
 * buildFloorPlanDetailTabSelectedEvent/buildViewFloorPlanClickedEvent below which share the
 * generic native_map_events name — same exception pattern as buildLotSelectedEvent/
 * buildCardsScrolledEvent above.
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @param {boolean} params.hasConfigurations - only set filter_name when true ("pass if true,
 *   blank if false" per the AC table)
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFloorPlanViewedEvent({ planId, specId, lotId, hasConfigurations }) {
  const customProperties = {
    plan_id: planId,
    spec_id: specId,
    lot_id: lotId,
  };

  if (hasConfigurations) {
    customProperties.filter_name = 'has_configurations';
  }

  return { eventName: 'native_map_floor_plan_viewed', customProperties };
}

/**
 * @param {object} params
 * @param {string} params.tabSelected - internal tab constant (constants.js)
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFloorPlanDetailTabSelectedEvent({ tabSelected, planId, specId, lotId }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'details_tab_selected',
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
      click_target1: getFloorPlanDetailTabTrackingValue(tabSelected),
    },
  };
}

/**
 * Mobile-only "View floor plan" button click (Floor Plan Configuration tab).
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildViewFloorPlanClickedEvent({ planId, specId, lotId }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: 'view_floor_plan_clicked',
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
    },
  };
}

/**
 * Floor Plan Configuration tab's "No configurations available" empty state shown. Gets its own
 * dedicated event name, like buildFloorPlanViewedEvent above.
 * @param {object} params
 * @param {number} params.planId
 * @param {number|null} params.specId
 * @param {number|null} params.lotId
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildEmptyConfigViewedEvent({ planId, specId, lotId }) {
  return {
    eventName: 'native_map_empty_config_viewed',
    customProperties: {
      lot_id: lotId,
      plan_id: planId,
      spec_id: specId,
      filter_name: 'no_config_available',
    },
  };
}

/**
 * "Similar homes available" accordion (under a Sold Home Card) expanded or collapsed.
 * Fires on both desktop and mobile — see plugins/tracking.plugin.js#onSimilarHomesToggled.
 * Reuses the generic native_map_events name with click_target/click_target2, like the other non-lot events
 * above, but also carries lot_id as its own raw property — the same mix buildPanelUpdatedEvent
 * above already uses (search_value/position/filter_name alongside click_target).
 * @param {object} params
 * @param {boolean} params.expanded
 * @param {number} params.lotId
 * @param {number} params.similarHomesCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSimilarHomesToggledEvent({ expanded, lotId, similarHomesCount }) {
  return {
    eventName: 'native_map_events',
    customProperties: {
      click_target: expanded ? 'similar_homes_expanded' : 'similar_homes_collapsed',
      lot_id: lotId,
      click_target2: similarHomesCount,
    },
  };
}

/**
 * A similar-home card (under a Sold Home Card's "Similar homes available" list) was clicked.
 * Fires on both desktop and mobile. Gets its own dedicated event name, like buildLotSelectedEvent
 * and buildCardsScrolledEvent above.
 * @param {object} params
 * @param {number} params.lotId
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.position - 1-based card position within the similar-homes list
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSimilarHomeCardClickedEvent({ lotId, planId, specId, position }) {
  return {
    eventName: 'native_map_smlr_hm_clicked',
    customProperties: { lot_id: lotId, plan_id: planId, spec_id: specId, current_card_position: position },
  };
}

/**
 * Mobile-only Sold list "See All" button clicked. Reuses the generic native_map_events name.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildSoldSeeAllClickedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'sold_lots_see_all_clicked' },
  };
}
